'use client';

import React from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import ClubRegistration from '@/ui/components/tiles/ClubRegistration';

const TRACKING_PREFIX = 'club-join-modal';

export interface ClubJoinModalProps {
  isOpen: boolean;
  isLoggedIn: boolean;
  // Where the signup came from, sent along with the registration.
  nag: string;
  onClose: () => void;
  onLogIn: () => void;
}

/**
 * Lets a visitor join Nuki Club without leaving the product. A logged out visitor logs in right
 * after joining (the login brings them back to the page); a logged in one can buy straight away.
 */
export function ClubJoinModal({
  isOpen,
  isLoggedIn,
  nag,
  onClose,
  onLogIn,
}: Readonly<ClubJoinModalProps>) {
  const t = useTranslations('ClubJoinModal');

  const joinScreen = { title: t('title'), subtitle: t('subtitle') };
  const welcomeScreen = isLoggedIn
    ? { title: t('welcomeTitle'), subtitle: t('loggedInSubtitle'), buttonText: t('continue') }
    : { title: t('welcomeTitle'), subtitle: t('logInSubtitle'), buttonText: t('logInToBuy') };

  return (
    // Above the product chooser overlay (z-50), like the notify me modal.
    <Dialog open={isOpen} className="relative z-60" onClose={onClose}>
      <div className="fixed inset-0 bg-neutral-100/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-4">
        <Dialog.Panel className="relative max-h-[95vh] w-full max-w-xl overflow-y-auto overscroll-contain rounded-2xl bg-neutral-5 shadow-xl">
          {/* Each screen's visible title labels the dialog. */}
          <ClubRegistration
            staticContent={{
              initialScreen: joinScreen,
              newAccountScreen: joinScreen,
              existingAccountScreen: joinScreen,
              welcomeMemberScreen: welcomeScreen,
            }}
            onWelcomeScreenAction={isLoggedIn ? onClose : onLogIn}
            shouldReloadAfterWelcomeScreenAction={false}
            isInDialog
            titleAs={Dialog.Title}
            nag={nag}
            trackingPrefix={TRACKING_PREFIX}
          />
          {/* After the form in the DOM so the dialog opens with focus on the first field. */}
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close')}
            className="absolute right-3 top-3 rounded-xl p-1 duration-200 ease-in hover:bg-neutral-10"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
